import React, { useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import {
  Box, Card, Typography, TextField, MenuItem, InputAdornment,
  TableContainer, Paper, Table, TableHead, TableRow, TableCell,
  TableBody, TablePagination, CircularProgress, Button, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CloseIcon from "@mui/icons-material/Close";
import { enqueueSnackbar } from "notistack";
import { useAuth } from "../../hooks/useAuth";
import {
  GET_NEFT_CREDIT_PAYMENTS,
  GET_NEFT_PENDING_VEHICLES,
  RAISE_KYC_FROM_NEFT_SESSION,
} from "./queries";

// Date fields come back as epoch-millisecond strings (same as Payment.date).
const formatEpoch = (value?: string | number | null) => {
  if (value === null || value === undefined || value === "") return "N/A";
  const ms = Number(value);
  if (Number.isNaN(ms)) return "N/A";
  return new Date(ms).toLocaleString();
};

const formatAmount = (value?: number | null) =>
  typeof value === "number"
    ? value.toLocaleString("en-IN", { style: "currency", currency: "INR" })
    : "N/A";

const headerCellSx = {
  backgroundColor: "#E60012",
  color: "#fff",
  fontWeight: "bold",
  fontSize: { xs: "0.7rem", sm: "0.8rem" },
  whiteSpace: "nowrap",
  padding: { xs: "6px", sm: "8px" },
  borderRight: "2px solid #e0e0e0",
  position: "sticky" as const,
  top: 0,
  zIndex: 11,
};

const cellStyle = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: 240,
  padding: { xs: "4px", sm: "6px" },
  fontSize: { xs: "0.72rem", sm: "0.82rem" },
  borderRight: "1px solid #e0e0e0",
};

const VEHICLE_SEARCH_TYPES = [
  { value: "vin", label: "VIN" },
  { value: "imei", label: "IMEI" },
  { value: "customer_name", label: "Customer Name" },
  { value: "session_id", label: "Session ID" },
  { value: "dealer_code", label: "Dealer Code" },
  { value: "engine_number", label: "Engine Number" },
];

// Shell shared by both panels: title + search (header), the scrolling table
// region (the TableContainer itself scrolls on BOTH axes), and a pinned footer.
const PanelCard: React.FC<{
  title: string;
  header: React.ReactNode;
  footer: React.ReactNode;
  minTableWidth: number;
  columns: string[];
  children: React.ReactNode;
}> = ({ title, header, footer, minTableWidth, columns, children }) => (
  <Card
    sx={{
      flex: 1,
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      boxShadow: 2,
      borderRadius: 3,
      bgcolor: "#fff",
      height: { xs: "70vh", md: "calc(100vh - 200px)" },
      overflow: "hidden",
    }}
  >
    <Box
      sx={{
        flexShrink: 0,
        background: "#fff",
        px: 2,
        pt: 2,
        pb: 1.5,
      }}
    >
      <Typography variant="h6" sx={{ mb: 1.5, fontSize: { xs: "1rem", sm: "1.15rem" } }}>
        {title}
      </Typography>
      {header}
    </Box>

    {/* Single scroll container — scrolls both x and y. minWidth on the table
        forces horizontal overflow so all columns stay reachable. */}
    <TableContainer
      component={Paper}
      sx={{ flex: 1, minHeight: 0, overflow: "auto", boxShadow: "none", borderRadius: 0 }}
    >
      <Table stickyHeader size="small" sx={{ minWidth: minTableWidth, width: "max-content" }}>
        <TableHead>
          <TableRow>
            {columns.map((h) => (
              <TableCell key={h} align="center" sx={headerCellSx}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>{children}</TableBody>
      </Table>
    </TableContainer>

    <Box sx={{ flexShrink: 0, borderTop: "1px solid #e0e0e0", overflowX: "auto" }}>
      {footer}
    </Box>
  </Card>
);

const NeftKyc = () => {
  const { canCreate } = useAuth();
  const canRaise = canCreate("KYC-DETAILS");
  const [faqOpen, setFaqOpen] = useState(false);

  // ---- NEFT credits (left panel) ----
  const [paySearch, setPaySearch] = useState("");
  const [payPage, setPayPage] = useState(0);
  const [payRpp, setPayRpp] = useState(20);

  const payments = useQuery(GET_NEFT_CREDIT_PAYMENTS, {
    variables: { limit: payRpp, offset: payPage * payRpp, searchTerm: paySearch.trim() },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const payRows = payments.data?.getNeftCreditPayments?.data ?? [];
  const payCount = payments.data?.getNeftCreditPayments?.count ?? 0;

  // ---- Pending vehicles (right panel) ----
  const [vehSearchType, setVehSearchType] = useState("vin");
  const [vehSearch, setVehSearch] = useState("");
  const [vehPage, setVehPage] = useState(0);
  const [vehRpp, setVehRpp] = useState(20);

  const vehicles = useQuery(GET_NEFT_PENDING_VEHICLES, {
    variables: {
      limit: vehRpp,
      offset: vehPage * vehRpp,
      searchTerm: vehSearch.trim(),
      searchType: vehSearchType,
    },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const vehRows = vehicles.data?.getNeftPendingVehicles?.data ?? [];
  const vehCount = vehicles.data?.getNeftPendingVehicles?.count ?? 0;

  // ---- Raise KYC mutation ----
  const [raiseKyc] = useMutation(RAISE_KYC_FROM_NEFT_SESSION);
  const [raisingId, setRaisingId] = useState<string | null>(null);

  const onRaise = async (sessionId: string) => {
    setRaisingId(sessionId);
    try {
      const { data } = await raiseKyc({ variables: { sessionId } });
      const r = data?.raiseKycFromNeftSession;
      if (r?.success) {
        enqueueSnackbar(r.message || "KYC raised successfully", { variant: "success" });
      } else if (r?.message && !r?.error) {
        // idempotent guard — already processed; treat as done, not a hard error.
        enqueueSnackbar(r.message, { variant: "info" });
      } else {
        enqueueSnackbar(r?.error || r?.message || "Failed to raise KYC", { variant: "error" });
      }
      // Refresh both lists — the vehicle drops out of "awaiting payment" on success.
      await Promise.all([payments.refetch(), vehicles.refetch()]);
    } catch (err: any) {
      enqueueSnackbar(err?.message || "Failed to raise KYC", { variant: "error" });
    } finally {
      setRaisingId(null);
    }
  };

  const payColumns = ["Sr.No", "Amount", "Remitter Name", "Remitter Bank", "IFSC", "User Ref No", "Description", "Txn Date", "Virtual Account"];
  const vehColumns = ["Sr.No", "VIN", "IMEI", "Customer", "Mobile", "Vehicle", "Dealer Code", "Dealer Name", "Status", "KYC Mode", "Action"];

  return (
    <Box sx={{ width: "100%" }}>
      {/* Title row with FAQ button */}
      <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" fontSize={{ xs: "1.4rem", sm: "2rem" }} sx={{ mb: 0.5 }}>
            NEFT KYC
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Match a received NEFT credit to a vehicle awaiting payment, then raise its KYC.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<HelpOutlineIcon />}
          onClick={() => setFaqOpen(true)}
          sx={{
            flexShrink: 0,
            textTransform: "none",
            borderColor: "#E60012",
            color: "#E60012",
            borderRadius: "20px",
            "&:hover": { borderColor: "#cc0010", backgroundColor: "#E600120A" },
          }}
        >
          How to use / FAQ
        </Button>
      </Box>

      <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2, alignItems: "stretch" }}>
        {/* LEFT — NEFT credits received */}
        <PanelCard
          title="NEFT Credits Received"
          minTableWidth={1100}
          columns={payColumns}
          header={
            <TextField
              size="small"
              fullWidth
              placeholder="Search remitter / description / reference / account"
              value={paySearch}
              onChange={(e) => { setPaySearch(e.target.value); setPayPage(0); }}
              sx={{ background: "#f5f5f5" }}
              InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }}
            />
          }
          footer={
            <TablePagination
              rowsPerPageOptions={[20, 50, 100]}
              component="div"
              count={payCount}
              rowsPerPage={payRpp}
              page={payPage}
              onPageChange={(_, p) => setPayPage(p)}
              onRowsPerPageChange={(e) => { setPayRpp(parseInt(e.target.value, 10)); setPayPage(0); }}
            />
          }
        >
          {payments.loading ? (
            <TableRow><TableCell colSpan={payColumns.length} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
          ) : payments.error ? (
            <TableRow><TableCell colSpan={payColumns.length} align="center" sx={{ py: 4, color: "#d32f2f" }}>{payments.error.message}</TableCell></TableRow>
          ) : payRows.length === 0 ? (
            <TableRow><TableCell colSpan={payColumns.length} align="center" sx={{ py: 4, color: "#888" }}>No NEFT credits found</TableCell></TableRow>
          ) : (
            payRows.map((row: any, idx: number) => (
              <TableRow key={row.id} hover sx={{ backgroundColor: idx % 2 === 0 ? "#fff" : "#f5f5f5" }}>
                <TableCell align="center" sx={cellStyle}>{payPage * payRpp + idx + 1}</TableCell>
                <TableCell align="right" sx={cellStyle}>{formatAmount(row.amount)}</TableCell>
                <TableCell align="left" sx={cellStyle}>{row.remitterName || "N/A"}</TableCell>
                <TableCell align="left" sx={cellStyle}>{row.remitterBank || "N/A"}</TableCell>
                <TableCell align="center" sx={cellStyle}>{row.ifscCode || "N/A"}</TableCell>
                <TableCell align="center" sx={cellStyle}>{row.userReferenceNumber || "N/A"}</TableCell>
                <TableCell align="left" sx={cellStyle}>
                  <Tooltip title={row.transactionDescription || ""} placement="top" arrow>
                    <span>{row.transactionDescription || "N/A"}</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="center" sx={cellStyle}>{formatEpoch(row.transactionDate)}</TableCell>
                <TableCell align="center" sx={cellStyle}>{row.virtualAccount || "N/A"}</TableCell>
              </TableRow>
            ))
          )}
        </PanelCard>

        {/* RIGHT — Vehicles awaiting payment */}
        <PanelCard
          title="Vehicles Awaiting Payment"
          minTableWidth={1250}
          columns={vehColumns}
          header={
            <Box display="flex" gap={1} flexWrap={{ xs: "wrap", sm: "nowrap" }}>
              <TextField
                select size="small" value={vehSearchType}
                onChange={(e) => { setVehSearchType(e.target.value); setVehPage(0); }}
                sx={{ minWidth: 140, background: "#f5f5f5", flex: "0 0 auto" }}
              >
                {VEHICLE_SEARCH_TYPES.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </TextField>
              <TextField
                size="small" fullWidth
                placeholder={`Search by ${VEHICLE_SEARCH_TYPES.find((o) => o.value === vehSearchType)?.label ?? ""}`}
                value={vehSearch}
                onChange={(e) => { setVehSearch(e.target.value); setVehPage(0); }}
                sx={{ background: "#f5f5f5", flex: 1 }}
                InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }}
              />
            </Box>
          }
          footer={
            <TablePagination
              rowsPerPageOptions={[20, 50, 100]}
              component="div"
              count={vehCount}
              rowsPerPage={vehRpp}
              page={vehPage}
              onPageChange={(_, p) => setVehPage(p)}
              onRowsPerPageChange={(e) => { setVehRpp(parseInt(e.target.value, 10)); setVehPage(0); }}
            />
          }
        >
          {vehicles.loading ? (
            <TableRow><TableCell colSpan={vehColumns.length} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
          ) : vehicles.error ? (
            <TableRow><TableCell colSpan={vehColumns.length} align="center" sx={{ py: 4, color: "#d32f2f" }}>{vehicles.error.message}</TableCell></TableRow>
          ) : vehRows.length === 0 ? (
            <TableRow><TableCell colSpan={vehColumns.length} align="center" sx={{ py: 4, color: "#888" }}>No vehicles awaiting payment</TableCell></TableRow>
          ) : (
            vehRows.map((row: any, idx: number) => (
              <TableRow key={row.sessionId} hover sx={{ backgroundColor: idx % 2 === 0 ? "#fff" : "#f5f5f5" }}>
                <TableCell align="center" sx={cellStyle}>{vehPage * vehRpp + idx + 1}</TableCell>
                <TableCell align="center" sx={cellStyle}>{row.vin || "N/A"}</TableCell>
                <TableCell align="center" sx={cellStyle}>{row.imei || "N/A"}</TableCell>
                <TableCell align="left" sx={cellStyle}>{row.customerName || "N/A"}</TableCell>
                <TableCell align="center" sx={cellStyle}>{row.customerMobile || "N/A"}</TableCell>
                <TableCell align="left" sx={cellStyle}>{[row.vehicleMake, row.vehicleModel].filter(Boolean).join(" ") || "N/A"}</TableCell>
                <TableCell align="center" sx={cellStyle}>{row.dealerCode || "N/A"}</TableCell>
                <TableCell align="left" sx={cellStyle}>{row.dealerName || "N/A"}</TableCell>
                <TableCell align="center" sx={cellStyle}>{row.status || "N/A"}</TableCell>
                <TableCell align="center" sx={cellStyle}>{row.kycMode || "N/A"}</TableCell>
                <TableCell align="center" sx={cellStyle}>
                  <Tooltip title={canRaise ? "" : "Requires KYC-DETAILS create access"} placement="top" arrow>
                    <span>
                      <Button
                        variant="contained"
                        disabled={!canRaise || raisingId === row.sessionId}
                        onClick={() => onRaise(row.sessionId)}
                        sx={{
                          backgroundColor: "#58B80F1A",
                          color: "#58B80F",
                          border: "1px solid #58B80F",
                          borderRadius: "20px",
                          height: "26px",
                          px: 1.5,
                          fontSize: "12px",
                          textTransform: "none",
                          whiteSpace: "nowrap",
                          "&:hover": { backgroundColor: "#58B80F33" },
                        }}
                      >
                        {raisingId === row.sessionId ? <CircularProgress size={14} sx={{ color: "#58B80F" }} /> : "Raise KYC"}
                      </Button>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))
          )}
        </PanelCard>
      </Box>

      {/* How-to / FAQ dialog */}
      <Dialog open={faqOpen} onClose={() => setFaqOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pr: 6 }}>
          <HelpOutlineIcon sx={{ color: "#E60012" }} />
          How to raise KYC for a NEFT payment
          <IconButton onClick={() => setFaqOpen(false)} sx={{ position: "absolute", right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box component="ol" sx={{ m: 0, pl: 3, "& li": { mb: 1, fontSize: "0.9rem", color: "#333" } }}>
            <li>
              NEFT credits arrive with <b>no vehicle reference</b>, so they can't be matched
              automatically — you match them by eye.
            </li>
            <li>
              In <b>NEFT Credits Received</b> (left), find the incoming payment. Note the{" "}
              <b>Remitter Name</b>, <b>Amount</b>, and dealer hints in the <b>Description</b>.
              Use the search box to filter by remitter, reference, or account.
            </li>
            <li>
              In <b>Vehicles Awaiting Payment</b> (right), locate the matching vehicle by{" "}
              <b>customer</b>, <b>dealer</b>, or <b>amount</b>. Switch the dropdown to search by
              VIN, IMEI, customer name, session ID, dealer code, or engine number.
            </li>
            <li>
              Scroll each table <b>sideways</b> to see all columns
              <SwapHorizIcon sx={{ fontSize: 16, verticalAlign: "middle", mx: 0.5, color: "#8d6e63" }} />
              (hold <b>Shift</b> + mouse wheel, or drag the scrollbar at the bottom of the table).
            </li>
            <li>
              When you're confident the credit belongs to a vehicle, click{" "}
              <b>Raise KYC</b> on that vehicle's row. On success it's marked{" "}
              <b>PAYMENT_RECEIVED</b> and drops off this list; both tables refresh automatically.
            </li>
            <li>
              If a row was already handled, you'll see an <i>"already processed"</i> notice —
              that's expected, not an error.
            </li>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFaqOpen(false)} sx={{ textTransform: "none", color: "#E60012" }}>
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NeftKyc;
